import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaWhatsapp, FaHeadset } from 'react-icons/fa';
import ChatBubble from './ChatBubble';
import ChatMessage from './ChatMessage';
import ChatOptions from './ChatOptions';
import { chatData } from './ChatData';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const WidgetContainer = styled.div`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 350px;
  max-width: calc(100vw - 40px);
  max-height: 500px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(55, 48, 163, 0.15);
  overflow: hidden;
  animation: ${slideIn} 0.3s ease;
  z-index: 9999;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    bottom: 80px;
    right: 10px;
    left: 10px;
    width: auto;
    max-height: calc(100vh - 120px);
  }

  @media (max-width: 480px) {
    bottom: 70px;
    max-height: calc(100vh - 100px);
  }
`;

const WidgetHeader = styled.div`
font-family: 'Times New Roman', serif;
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 100%);
  color: white;
  padding: 1rem 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-shrink: 0;
  
  h3 {
    font-size: 1rem;
    margin: 0;
    font-weight: 700;
  }
  
  p {
    font-size: 0.7rem;
    opacity: 0.85;
    margin: 0;
  }

  @media (max-width: 480px) {
    padding: 0.8rem;
  }
`;

const HeaderIcon = styled.div`
  width: 38px;
  height: 38px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
  border: 2px solid rgba(255, 217, 102, 0.3);
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: #f8f7fc;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #3730A3;
    border-radius: 4px;
  }
`;

const InputContainer = styled.div`
  padding: 0.8rem;
  border-top: 1px solid rgba(55, 48, 163, 0.08);
  background: white;
  flex-shrink: 0;
`;

const QuickReplies = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const QuickReply = styled.button`
font-family: 'Times New Roman', serif;
  background: #f0efff;
  border: 1px solid #d0d0e0;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  color: #3730A3;
  font-weight: 500;
  
  &:hover {
    background: #3730A3;
    color: white;
    border-color: #3730A3;
  }
`;

const CallbackForm = styled.div`
  padding: 1rem;
  background: #f8f7fc;
  border-radius: 10px;
  margin: 0.5rem 0;
  border: 1px solid rgba(55, 48, 163, 0.1);
`;

const FormInput = styled.input`
font-family: 'Times New Roman', serif;
  width: 100%;
  padding: 0.7rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 0.8rem;
  font-size: 0.85rem;
  
  &:focus {
    outline: none;
    border-color: #3730A3;
    box-shadow: 0 0 0 3px rgba(55, 48, 163, 0.08);
  }
`;

const FormButton = styled.button`
font-family: 'Times New Roman', serif;
  background: #3730A3;
  color: white;
  border: none;
  padding: 0.7rem;
  border-radius: 8px;
  width: 100%;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #0b0b77;
  }
`;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [history, setHistory] = useState([]);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const widgetRef = useRef(null);

  const whatsappNumber = "9000013099";
  const whatsappMessage = "Hello%21%20I%27m%20interested%20in%20your%20courses.%20Can%20you%20please%20help%20me%3F";

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ text: chatData.greeting.message, isUser: false }]);
      setCurrentOptions(chatData.greeting.options);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleOptionSelect = (option) => {
    const userMessage = option;
    setMessages(prev => [...prev, { text: userMessage, isUser: true }]);

    // Handle back navigation
    if (option.includes("← Back")) {
      if (history.length > 0) {
        const prevState = history[history.length - 1];
        const newHistory = history.slice(0, -1);
        setHistory(newHistory);
        
        setTimeout(() => {
          setMessages(prev => [...prev, { text: prevState.message, isUser: false }]);
          setCurrentOptions(prevState.options || []);
        }, 400);
      } else {
        // If no history, go to greeting
        setTimeout(() => {
          setMessages(prev => [...prev, { text: chatData.greeting.message, isUser: false }]);
          setCurrentOptions(chatData.greeting.options);
        }, 400);
      }
      return;
    }

    const response = chatData.responses[option];

    if (response) {
      // Save current state to history before navigating
      setHistory(prev => [...prev, {
        message: currentOptions.length > 0 ? messages[messages.length - 1]?.text || chatData.greeting.message : chatData.greeting.message,
        options: currentOptions
      }]);

      setTimeout(() => {
        setMessages(prev => [...prev, { text: response.message, isUser: false }]);

        if (response.action === "whatsapp") {
          window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
          setCurrentOptions(chatData.greeting.options);
        } else if (response.action === "callback-form") {
          setShowCallbackForm(true);
          setCurrentOptions([]);
        } else if (response.action === "email") {
          window.open('mailto:info@learnthruus.com', '_blank');
          setCurrentOptions(chatData.greeting.options);
        } else if (response.action === "email-request") {
          setCurrentOptions(["← Back"]);
        } else if (response.options) {
          setCurrentOptions(response.options);
        } else {
          setCurrentOptions([]);
        }
      }, 500);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  const handleCallbackSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get('name');
    const phone = formData.get('phone');

    const message = `New Callback Request:%0AName: ${name}%0APhone: ${phone}`;
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');

    setMessages(prev => [...prev, {
      text: "✅ Thank you! We'll call you back within 2 hours.",
      isUser: false
    }]);
    setShowCallbackForm(false);
    setCurrentOptions(chatData.greeting.options);
  };

  const handleQuickReply = (reply) => {
    handleOptionSelect(reply);
  };

  return (
    <>
      <ChatBubble
        open={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        onWhatsApp={handleWhatsApp}
      />

      {isOpen && (
        <WidgetContainer ref={widgetRef}>
          <WidgetHeader>
            <HeaderIcon>
              <FaHeadset />
            </HeaderIcon>
            <div>
              <h3>Learn Thru Us Academy</h3>
              <p>🟢 Online | Typically replies instantly</p>
            </div>
          </WidgetHeader>

          <MessagesContainer ref={messagesContainerRef}>
            {messages.map((msg, index) => (
              <ChatMessage
                key={index}
                message={msg.text}
                isUser={msg.isUser}
              />
            ))}

            {showCallbackForm && (
              <CallbackForm>
                <form onSubmit={handleCallbackSubmit}>
                  <FormInput
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    required
                  />
                  <FormInput
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    required
                  />
                  <FormButton type="submit">
                    📞 Request Call Back
                  </FormButton>
                </form>
              </CallbackForm>
            )}

            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputContainer>
            {currentOptions.length > 0 && !showCallbackForm && (
              <ChatOptions
                options={currentOptions}
                onSelect={handleOptionSelect}
                onWhatsApp={handleWhatsApp}
              />
            )}

            {!showCallbackForm && (
              <QuickReplies>
                {chatData.quickReplies.map((reply, index) => (
                  <QuickReply
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                  >
                    {reply}
                  </QuickReply>
                ))}
              </QuickReplies>
            )}
          </InputContainer>
        </WidgetContainer>
      )}
    </>
  );
};

export default ChatWidget;